import { Button, Checkbox, Input, InputNumber, message, Select, Table, Upload } from "antd";
import { collection, doc, onSnapshot, setDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { FinanceEntryType, ReconciliationRuleType } from "../typeDefs";
import { isInGstRegisteredPeriod } from "./taxConfig";

const financeCategories: Record<string, string[]> = {
  Revenue: [
    "Sales Revenue",
    "Service Revenue",
    "Interest Income",
    "Dividend Income",
    "Rental Income",
  ],
  Expenses: [
    "Cost of Goods Sold",
    "Salaries and Wages",
    "Shareholder Salary",
    "Rent Expense",
    "Utilities Expense",
    "Depreciation Expense",
    "Interest Expense",
    "Insurance Expense",
    "Advertising Expense",
    "Subscriptions",
    "Travel and Entertainment",
    "Miscellaneous Expenses",
    "Professional Fees",
    "Maintenance and Repairs",
    "Research and Development",
    "Training and Development",
    "Office Supplies Expense",
  ],
  Assets: [
    "Cash and Cash Equivalents",
    "Accounts Receivable",
    "Inventory",
    "Prepaid Expenses",
    "Property, Plant, and Equipment",
    "Intangible Assets",
    "Investments",
    "Office Fit-Out",
    "Other Current Assets",
  ],
  Liabilities: [
    "Accounts Payable",
    "GST Payable",
    "GST Payment",
    "Income Tax Payable",
    "Income Tax Payment",
    "Provisional Tax Payment",
    "PAYE/Withholding Tax Payment",
    "Short-term Debt",
    "Long-term Debt",
    "Loan Received",
    "Loan Repayment",
    "Accrued Liabilities",
    "Deferred Revenue",
    "Notes Payable",
  ],
  Equity: [
    "Common Stock",
    "Shareholder Drawings",
    "Owner Contribution",
    "Current Account Adjustment",
    "Preferred Stock",
    "Retained Earnings",
    "Additional Paid-in Capital",
    "Treasury Stock",
  ],
  "Non-Business": [
    "Personal / Other Account",
    "Cash Not Banked",
    "Duplicate / Imported In Error",
    "Not Company Spend",
    "Exclude Pending Deletion",
  ],
};

type DraftTransaction = {
  key: string;
  bankTransactionId: string;
  date: number;
  description: string;
  vendor: string;
  originalAmount: number;
  amount: number;
  category: string;
  subCategory: string;
  gstIncluded: boolean;
  gstRegistered: boolean;
  gstNumber: string | null;
  businessUseRate: number;
  isHomeOfficeExpense: boolean;
  selected: boolean;
  needsReview: boolean;
  reviewed: boolean;
  existingMatchSummary?: string;
  confidence: "auto" | "suggested" | "manual";
};

const parseOfxDate = (value?: string) => {
  if (!value) return Date.now();
  const clean = value.trim();
  const year = Number(clean.slice(0, 4));
  const month = Number(clean.slice(4, 6));
  const day = Number(clean.slice(6, 8));

  if ([year, month, day].some((part) => Number.isNaN(part))) {
    return parseDate(value);
  }

  return new Date(year, month - 1, day).getTime();
};

const getOfxTag = (block: string, tag: string) => {
  const paired = block.match(new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, "i"));
  if (paired?.[1]) return paired[1].trim();

  const sgml = block.match(new RegExp(`<${tag}>([^<\\r\\n]+)`, "i"));
  return sgml?.[1]?.trim() || "";
};

const parseOfx = (text: string) => {
  const matches = text.match(/<STMTTRN>[\s\S]*?(?=<STMTTRN>|<\/BANKTRANLIST>|<\/CREDITCARDMSGSRSV1>|$)/gi) || [];

  return matches.map((block) => {
    const date = parseOfxDate(getOfxTag(block, "DTPOSTED"));
    const amount = parseMoney(getOfxTag(block, "TRNAMT"));
    const fitId = getOfxTag(block, "FITID");
    const name = getOfxTag(block, "NAME");
    const memo = getOfxTag(block, "MEMO");
    const trnType = getOfxTag(block, "TRNTYPE");
    const description = [name, memo].filter(Boolean).join(" - ") || trnType || "OFX transaction";

    return {
      date,
      description,
      signedAmount: amount,
      bankTransactionId: fitId ? normalise(`ofx_${fitId}`) : makeTransactionId(date, description, amount),
    };
  });
};

const parseCsv = (text: string) => {
  const rows: string[][] = [];
  let current = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      row.push(current.trim());
      current = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(current.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      current = "";
    } else {
      current += char;
    }
  }

  row.push(current.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
};

const parseMoney = (value?: string) => {
  if (!value) return 0;
  const negative = value.includes("(") && value.includes(")");
  const parsed = Number(value.replace(/[,$()\s]/g, ""));
  if (Number.isNaN(parsed)) return 0;
  return negative ? -Math.abs(parsed) : parsed;
};

const parseDate = (value?: string) => {
  if (!value) return Date.now();
  const clean = value.trim();
  const parts = clean.split(/[/-]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    const [a, b, c] = parts;
    const year = c < 100 ? 2000 + c : c;

    // NZ bank exports are commonly dd/mm/yyyy. ISO yyyy-mm-dd also supported.
    if (a > 31) return new Date(a, b - 1, c).getTime();
    return new Date(year, b - 1, a).getTime();
  }

  const parsed = new Date(clean).getTime();
  return Number.isNaN(parsed) ? Date.now() : parsed;
};

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const makeTransactionId = (date: number, description: string, amount: number) =>
  normalise(`${date}_${description}_${amount.toFixed(2)}`);

const cleanForFirestore = <T extends Record<string, unknown>>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined)) as T;

const findColumn = (headers: string[], names: string[]) =>
  headers.findIndex((header) =>
    names.some((name) => header.toLowerCase().includes(name.toLowerCase())),
  );

const guessVendor = (description: string) =>
  description
    .replace(/\b(eftpos|visa|mastercard|debit|credit|payment|transfer|online|bank)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);

export const BankCsvImporter = ({ data }: { data: FinanceEntryType[] }) => {
  const [drafts, setDrafts] = useState<DraftTransaction[]>([]);
  const [importing, setImporting] = useState(false);
  const [rules, setRules] = useState<ReconciliationRuleType[]>([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "reconciliationRules"), (snapshot) => {
      setRules(snapshot.docs.map((item) => ({ ...item.data(), id: item.id } as ReconciliationRuleType)));
    });

    return unsub;
  }, []);

  const importedIds = useMemo(
    () => new Set(data.map((item) => item.bankTransactionId).filter(Boolean)),
    [data],
  );

  const learnedRules = useMemo(
    () =>
      data
        .filter((item) => !item.isDeleted && item.vendor)
        .map((item) => ({
          vendor: item.vendor,
          token: normalise(item.vendor),
          category: item.category,
          subCategory: item.subCategory,
          gstIncluded: item.gstIncluded ?? false,
          gstRegistered: item.gstRegistered ?? false,
          gstNumber: item.gstNumber || null,
          businessUseRate: item.businessUseRate ?? 100,
          isHomeOfficeExpense: item.isHomeOfficeExpense ?? false,
        }))
        .filter((item) => item.token.length > 2),
    [data],
  );

  const reconcile = (description: string, amount: number) => {
    const descToken = normalise(description);

    if (amount < 0 && /(shareholder|owner|director).*(salary|wage|payroll)|salary|wages|payroll/i.test(description)) {
      return {
        vendor: guessVendor(description) || "Shareholder Salary",
        category: "Expenses",
        subCategory: "Shareholder Salary",
        gstIncluded: false,
        gstRegistered: false,
        gstNumber: null,
        businessUseRate: 100,
        isHomeOfficeExpense: false,
        confidence: "suggested" as const,
      };
    }

    if (amount < 0 && /(draw|drawings|shareholderdraw|ownerdraw)/i.test(description)) {
      return {
        vendor: guessVendor(description) || "Shareholder Drawings",
        category: "Equity",
        subCategory: "Shareholder Drawings",
        gstIncluded: false,
        gstRegistered: false,
        gstNumber: null,
        businessUseRate: 100,
        isHomeOfficeExpense: false,
        confidence: "suggested" as const,
      };
    }

    if (amount < 0 && /(ird|inland revenue|gst|goods and services tax|income tax|provisional tax|paye|withholding tax)/i.test(description)) {
      const lower = description.toLowerCase();
      const subCategory = lower.includes("gst") || lower.includes("goods and services")
        ? "GST Payment"
        : lower.includes("provisional")
          ? "Provisional Tax Payment"
          : lower.includes("paye") || lower.includes("withholding")
            ? "PAYE/Withholding Tax Payment"
            : "Income Tax Payment";

      return {
        vendor: guessVendor(description) || "IRD",
        category: "Liabilities",
        subCategory,
        gstIncluded: false,
        gstRegistered: false,
        gstNumber: null,
        businessUseRate: 100,
        isHomeOfficeExpense: false,
        confidence: "suggested" as const,
      };
    }
    const savedRule = rules
      .filter((rule) => !rule.isDeleted)
      .find((rule) => descToken.includes(normalise(rule.matchText)));

    if (savedRule) {
      return {
        vendor: savedRule.vendor,
        category: savedRule.category,
        subCategory: savedRule.subCategory,
        gstIncluded: savedRule.gstIncluded ?? false,
        gstRegistered: savedRule.gstRegistered ?? false,
        gstNumber: savedRule.gstNumber || null,
        businessUseRate: savedRule.businessUseRate ?? 100,
        isHomeOfficeExpense: savedRule.isHomeOfficeExpense ?? false,
        confidence: "auto" as const,
      };
    }

    const learned = learnedRules.find(
      (rule) => descToken.includes(rule.token) || rule.token.includes(descToken),
    );

    if (learned) {
      return {
        ...learned,
        businessUseRate: learned.businessUseRate ?? 100,
        isHomeOfficeExpense: learned.isHomeOfficeExpense ?? false,
        confidence: "auto" as const,
      };
    }

    return amount >= 0
      ? {
          vendor: guessVendor(description) || "Bank Deposit",
          category: "Revenue",
          subCategory: "Service Revenue",
          gstIncluded: true,
          gstRegistered: true,
          gstNumber: null,
          businessUseRate: 100,
          isHomeOfficeExpense: false,
          confidence: "suggested" as const,
        }
      : {
          vendor: guessVendor(description) || "Bank Payment",
          category: "Expenses",
          subCategory: "Miscellaneous Expenses",
          gstIncluded: true,
          gstRegistered: true,
          gstNumber: null,
          businessUseRate: 100,
          isHomeOfficeExpense: false,
          confidence: "suggested" as const,
        };
  };

  const toDraft = ({
    date,
    description,
    signedAmount,
    bankTransactionId,
  }: {
    date: number;
    description: string;
    signedAmount: number;
    bankTransactionId: string;
  }) => {
    const originalAmount = Math.abs(signedAmount);
    const suggestion = reconcile(description, signedAmount);
    const isGstPeriod = isInGstRegisteredPeriod(date);
    const businessUseRate = suggestion.businessUseRate ?? 100;
    const claimAmount =
      suggestion.category === "Expenses" || suggestion.category === "Assets"
        ? originalAmount * (businessUseRate / 100)
        : originalAmount;
    const possibleExistingMatch = data.find(
      (item) =>
        !item.isDeleted &&
        item.date === date &&
        Math.abs(item.amount - claimAmount) < 0.01,
    );
    const isExactImportedDuplicate = importedIds.has(bankTransactionId);

    return {
      key: bankTransactionId,
      bankTransactionId,
      date,
      description,
      vendor: suggestion.vendor,
      originalAmount,
      amount: claimAmount,
      category: suggestion.category,
      subCategory: suggestion.subCategory,
      gstIncluded: isGstPeriod ? suggestion.gstIncluded ?? false : false,
      gstRegistered: isGstPeriod ? suggestion.gstRegistered ?? false : false,
      gstNumber: suggestion.gstNumber || null,
      businessUseRate,
      isHomeOfficeExpense: suggestion.isHomeOfficeExpense ?? false,
      selected: !isExactImportedDuplicate && !possibleExistingMatch,
      needsReview: !!possibleExistingMatch,
      reviewed: false,
      existingMatchSummary: possibleExistingMatch
        ? `${possibleExistingMatch.vendor || "Existing entry"} / ${possibleExistingMatch.description || "No description"}`
        : undefined,
      confidence: isExactImportedDuplicate || possibleExistingMatch ? "manual" : suggestion.confidence,
    } as DraftTransaction;
  };

  const handleBankFile = async (file: File) => {
    const text = await file.text();
    const isOfx = file.name.toLowerCase().endsWith(".ofx") || text.includes("<OFX>");

    if (isOfx) {
      const ofxRows = parseOfx(text);

      if (!ofxRows.length) {
        message.error("No transactions found in OFX file.");
        return Upload.LIST_IGNORE;
      }

      const parsed = ofxRows.map(toDraft);
      setDrafts(parsed);
      message.success(`Loaded ${parsed.length} OFX transactions.`);
      return false;
    }

    const rows = parseCsv(text);

    if (rows.length < 2) {
      message.error("No transactions found in CSV.");
      return Upload.LIST_IGNORE;
    }

    const headers = rows[0].map((header) => header.trim());
    const dateIndex = findColumn(headers, ["date", "posted"]);
    const descriptionIndex = findColumn(headers, ["description", "details", "particulars", "payee", "narrative"]);
    const amountIndex = findColumn(headers, ["amount", "value"]);
    const debitIndex = findColumn(headers, ["debit", "withdrawal", "paid out"]);
    const creditIndex = findColumn(headers, ["credit", "deposit", "paid in"]);

    if (dateIndex < 0 || descriptionIndex < 0 || (amountIndex < 0 && debitIndex < 0 && creditIndex < 0)) {
      message.error("Could not detect Date, Description and Amount columns.");
      return Upload.LIST_IGNORE;
    }

    const parsed = rows.slice(1).map((row) => {
      const date = parseDate(row[dateIndex]);
      const description = row[descriptionIndex] || "Bank transaction";
      const debit = parseMoney(row[debitIndex]);
      const credit = parseMoney(row[creditIndex]);
      const rawAmount = amountIndex >= 0 ? parseMoney(row[amountIndex]) : credit - debit;
      const signedAmount = rawAmount || credit - debit;
      const bankTransactionId = makeTransactionId(date, description, signedAmount);

      return toDraft({ date, description, signedAmount, bankTransactionId });
    });

    setDrafts(parsed);
    message.success(`Loaded ${parsed.length} bank transactions.`);
    return false;
  };

  const updateDraft = (key: string, patch: Partial<DraftTransaction>) => {
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.key === key
          ? {
              ...draft,
              ...patch,
              ...(patch.category
                ? {
                    subCategory: financeCategories[patch.category]?.[0] || draft.subCategory,
                    amount:
                      patch.category === "Expenses" || patch.category === "Assets"
                        ? draft.originalAmount * (draft.businessUseRate / 100)
                        : draft.originalAmount,
                  }
                : {}),
              ...(typeof patch.businessUseRate === "number"
                ? {
                    amount:
                      (patch.category || draft.category) === "Expenses" ||
                      (patch.category || draft.category) === "Assets"
                        ? draft.originalAmount * (patch.businessUseRate / 100)
                        : draft.originalAmount,
                  }
                : {}),
              ...(typeof patch.originalAmount === "number"
                ? {
                    amount:
                      (patch.category || draft.category) === "Expenses" ||
                      (patch.category || draft.category) === "Assets"
                        ? patch.originalAmount * (draft.businessUseRate / 100)
                        : patch.originalAmount,
                  }
                : {}),
              confidence: "manual",
            }
          : draft,
      ),
    );
  };

  const importSelected = async () => {
    const selected = drafts.filter(
      (draft) =>
        draft.selected &&
        !importedIds.has(draft.bankTransactionId) &&
        (!draft.needsReview || draft.reviewed),
    );
    const unreviewed = drafts.filter((draft) => draft.selected && draft.needsReview && !draft.reviewed);

    if (unreviewed.length) {
      message.warning("Some selected transactions match existing entries. Please tick Reviewed before importing them.");
      return;
    }

    if (!selected.length) {
      message.warning("No new selected transactions to import.");
      return;
    }

    setImporting(true);
    try {
      await Promise.all(
        selected.map((draft) => {
          const id = nanoid();
          return setDoc(doc(db, `/finances/${id}`), cleanForFirestore({
            id,
            description: draft.description,
            category: draft.category,
            subCategory: draft.subCategory,
            date: draft.date,
            amount: draft.amount,
            originalAmount: draft.originalAmount,
            businessUseRate: draft.businessUseRate,
            isHomeOfficeExpense: draft.isHomeOfficeExpense ?? false,
            apportionmentSource: "Business Account",
            apportionMethod:
              draft.category === "Expenses" || draft.category === "Assets"
                ? draft.businessUseRate <= 0
                  ? "None"
                  : draft.businessUseRate < 100
                    ? "Apportionment Method"
                    : "Principal Method"
                : null,
            apportionRate:
              draft.category === "Expenses" || draft.category === "Assets"
                ? draft.businessUseRate ?? 100
                : 0,
            gstIncluded: draft.gstIncluded ?? false,
            gstRegistered: draft.gstRegistered ?? false,
            gstNumber: draft.gstNumber || null,
            vendor: draft.vendor,
            image: null,
            isDeleted: false,
            receipt: false,
            bankTransactionId: draft.bankTransactionId,
            bankSource: "csv",
            reconciledAt: Date.now(),
            reconciliationConfidence: draft.confidence || "manual",
          } as FinanceEntryType));
        }),
      );

      message.success(`Imported ${selected.length} reconciled transactions.`);
      setDrafts([]);
    } catch (err) {
      console.error(err);
      message.error("Could not import transactions.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Upload accept=".csv,.ofx,text/csv,application/x-ofx" beforeUpload={handleBankFile} showUploadList={false}>
        <Button>Select bank CSV or OFX</Button>
      </Upload>

      <Table
        size="small"
        pagination={{ pageSize: 8 }}
        dataSource={drafts}
        scroll={{ x: 1530 }}
        columns={[
          {
            title: "Import",
            dataIndex: "selected",
            width: 70,
            render: (_value, record) => (
              <Checkbox
                checked={record.selected}
                disabled={importedIds.has(record.bankTransactionId)}
                onChange={(e) => updateDraft(record.key, { selected: e.target.checked })}
              />
            ),
          },
          {
            title: "Review",
            dataIndex: "needsReview",
            width: 180,
            render: (value, record) =>
              value ? (
                <div>
                  <div style={{ color: "#d48806", fontSize: 12 }}>Possible existing match</div>
                  <div style={{ fontSize: 11 }}>{record.existingMatchSummary}</div>
                  <Checkbox
                    checked={record.reviewed}
                    onChange={(e) => updateDraft(record.key, { reviewed: e.target.checked })}
                  >
                    Reviewed
                  </Checkbox>
                </div>
              ) : (
                "-"
              ),
          },
          {
            title: "Date",
            dataIndex: "date",
            width: 110,
            render: (value) => new Date(value).toLocaleDateString("en-GB"),
          },
          {
            title: "Description",
            dataIndex: "description",
            width: 220,
          },
          {
            title: "Vendor",
            dataIndex: "vendor",
            width: 180,
            render: (value, record) => (
              <Input value={value} onChange={(e) => updateDraft(record.key, { vendor: e.target.value })} />
            ),
          },
          {
            title: "Original Amount",
            dataIndex: "originalAmount",
            width: 130,
            render: (value, record) => (
              <InputNumber
                min={0}
                value={value}
                onChange={(next) => updateDraft(record.key, { originalAmount: Number(next || 0) })}
              />
            ),
          },
          {
            title: "Business %",
            dataIndex: "businessUseRate",
            width: 120,
            render: (value, record) => (
              <InputNumber
                min={0}
                max={100}
                value={value}
                onChange={(next) => updateDraft(record.key, { businessUseRate: Number(next || 0) })}
              />
            ),
          },
          {
            title: "Claim Amount",
            dataIndex: "amount",
            width: 120,
            render: (value) => `$${Number(value || 0).toFixed(2)}`,
          },
          {
            title: "Category",
            dataIndex: "category",
            width: 160,
            render: (value, record) => (
              <Select value={value} style={{ width: "100%" }} onChange={(next) => updateDraft(record.key, { category: next })}>
                {Object.keys(financeCategories).map((category) => (
                  <Select.Option key={category} value={category}>{category}</Select.Option>
                ))}
              </Select>
            ),
          },
          {
            title: "Sub Category",
            dataIndex: "subCategory",
            width: 210,
            render: (value, record) => (
              <Select value={value} style={{ width: "100%" }} onChange={(next) => updateDraft(record.key, { subCategory: next })}>
                {(financeCategories[record.category] || []).map((category) => (
                  <Select.Option key={category} value={category}>{category}</Select.Option>
                ))}
              </Select>
            ),
          },
          {
            title: "Home Office",
            dataIndex: "isHomeOfficeExpense",
            width: 110,
            render: (value, record) => (
              <Checkbox checked={value} onChange={(e) => updateDraft(record.key, { isHomeOfficeExpense: e.target.checked })} />
            ),
          },
          {
            title: "GST",
            dataIndex: "gstIncluded",
            width: 80,
            render: (value, record) => (
              <Checkbox checked={value} onChange={(e) => updateDraft(record.key, { gstIncluded: e.target.checked })} />
            ),
          },
          {
            title: "Match",
            dataIndex: "confidence",
            width: 90,
          },
        ]}
      />

      <Button type="primary" loading={importing} onClick={importSelected} disabled={!drafts.length}>
        Import selected reconciled transactions
      </Button>
    </div>
  );
};
